import { CrashMonitor } from './crashMonitor';
import type {
  CrashMonitorConfig,
  HeartbeatData,
  ServiceWorkerMessage,
} from '../types';
import { logger as moduleLogger } from '@/crashReports/utils/logger';
const logger = moduleLogger.createSubLogger('ServiceWorkerCrashMonitor');

export class ServiceWorkerCrashMonitor {
  private registration: ServiceWorkerRegistration | null = null;
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private visibilityChangeHandler: (() => void) | null = null;
  private pageHideHandler: (() => void) | null = null;
  private config: CrashMonitorConfig;
  private sessionId: string;
  private isInitialized = false;
  private webWorkerMonitor: CrashMonitor | null = null;
  private useWebWorkerFallback = false;

  constructor(config: Partial<CrashMonitorConfig> = {}) {
    this.config = {
      heartbeatInterval: 3000, // 3秒心跳间隔，适合Service Worker
      crashTimeout: 10000, // 10秒崩溃超时
      reportEndpoint: '/client/error/reports',
      ...config,
    };

    // 生成会话ID
    this.sessionId = this.generateSessionId();
  }

  /**
   * 启动崩溃监测
   */
  async start(): Promise<void> {
    if (this.isInitialized) {
      logger.warn('already started');
      return;
    }

    try {
      // 注册 Service Worker
      await this.startWithServiceWorker();

      // 开始心跳
      this.startHeartbeat();

      // 添加页面可见性监听器
      this.addVisibilityListeners();
      this.isInitialized = true;

      logger.log('started successfully');
    } catch (error) {
      logger.warn('启动失败，降级到 Web Worker:', error);
      this.useWebWorkerFallback = true;
      // 降级到 Web Worker
      await this.startWithWebWorker();
    }
  }

  private async startWithServiceWorker(): Promise<void> {
    if (!this.registration?.active) {
      await this.registerServiceWorker();
      await this.waitForServiceWorkerActivation();
      await this.initServiceWorker();
    }
  }

  private async startWithWebWorker(): Promise<void> {
    // 降级到 Web Worker 的逻辑
    import('./crashMonitor').then(({ CrashMonitor }) => {
      this.webWorkerMonitor = new CrashMonitor(this.config);
      return this.webWorkerMonitor.start();
    });
  }

  /**
   * 停止崩溃监测
   */
  stop(): void {
    if (!this.isInitialized) {
      return;
    }

    if (this.useWebWorkerFallback) {
      this.webWorkerMonitor?.stop();
      return;
    }

    // 停止心跳
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }

    // 移除页面可见性监听器
    this.removeVisibilityListeners();

    // 停止 Service Worker
    if (this.registration?.active) {
      this.registration.active.postMessage({ type: 'stop' });
    }

    this.isInitialized = false;
    logger.info('stopped');
  }

  getHeartbeatData(): HeartbeatData {
    return {
      timestamp: Date.now(),
      sessionId: this.sessionId,
      pageUrl: window.location.href,
      userAgent: navigator.userAgent,
    };
  }

  /**
   * 手动发送心跳
   */
  sendHeartbeat(): void {
    if (!this.isInitialized || !this.registration?.active) {
      return;
    }

    const heartbeatData: HeartbeatData = this.getHeartbeatData();

    this.registration.active.postMessage({
      type: 'heartbeat',
      data: heartbeatData,
    });
  }

  /**
   * 获取会话ID
   */
  getSessionId(): string {
    return this.sessionId;
  }

  /**
   * 更新配置
   */
  updateConfig(config: Partial<CrashMonitorConfig>): void {
    this.config = { ...this.config, ...config };
    if (this.registration?.active) {
      this.registration.active.postMessage({
        type: 'init',
        config: this.config,
      });
    }
  }

  /**
   * 注册 Service Worker
   */
  private async registerServiceWorker(): Promise<void> {
    if (!('serviceWorker' in navigator)) {
      throw new Error('Service Worker is not supported');
    }

    try {
      this.registration = await navigator.serviceWorker.register(
        '/crashReportServiceWorker.worker.js',
        {
          scope: '/',
        }
      );

      logger.info('Service Worker registered:', this.registration);
    } catch (error) {
      logger.error('Service Worker registration failed:', error);
      throw error;
    }
  }

  /**
   * 等待 Service Worker 激活
   */
  private async waitForServiceWorkerActivation(): Promise<void> {
    if (!this.registration) return;

    return new Promise((resolve) => {
      if (this.registration!.active) {
        resolve();
        return;
      }

      const handleStateChange = () => {
        if (this.registration!.active) {
          this.registration!.removeEventListener(
            'updatefound',
            handleStateChange
          );
          resolve();
        }
      };

      this.registration!.addEventListener('updatefound', handleStateChange);

      // 如果 Service Worker 正在等待激活，强制激活
      if (this.registration!.waiting) {
        this.registration!.waiting.postMessage({ type: 'skipWaiting' });
      }
    });
  }

  /**
   * 初始化 Service Worker
   */
  private async initServiceWorker(): Promise<void> {
    if (!this.registration?.active) return;

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Service Worker initialization timeout'));
      }, 10000);

      const handleMessage = (event: MessageEvent<ServiceWorkerMessage>) => {
        if (event.data.type === 'initialized') {
          clearTimeout(timeout);
          navigator.serviceWorker.removeEventListener('message', handleMessage);
          resolve();
        }
      };

      navigator.serviceWorker.addEventListener('message', handleMessage);

      // 发送初始化消息
      this.registration!.active!.postMessage({
        type: 'init',
        config: this.config,
      });
    });
  }

  /**
   * 开始心跳
   */
  private startHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }

    // 立即发送一次心跳
    this.sendHeartbeat();

    // 设置定时心跳
    this.heartbeatInterval = setInterval(() => {
      this.sendHeartbeat();
    }, this.config.heartbeatInterval);
  }

  /**
   * 添加页面可见性监听器
   */
  private addVisibilityListeners(): void {
    if (typeof document === 'undefined') return;

    // 页面可见性变化监听
    this.visibilityChangeHandler = () => {
      if (document.hidden) {
        // 页面隐藏时，立即发送心跳并通知Service Worker
        this.sendHeartbeat();
        if (this.registration?.active) {
          this.registration.active.postMessage({
            type: 'pageHidden',
            timestamp: Date.now(),
          });
        }
        logger.log('页面隐藏，暂停心跳监测');
      } else {
        // 页面显示时，立即发送心跳并恢复监测
        this.sendHeartbeat();
        if (this.registration?.active) {
          this.registration.active.postMessage({
            type: 'pageVisible',
            timestamp: Date.now(),
          });
        }
        logger.log('页面显示，恢复心跳监测');
      }
    };

    // 页面卸载监听
    this.pageHideHandler = () => {
      // 页面卸载时发送最后一次心跳
      this.sendHeartbeat();
      logger.log('页面卸载，发送最后心跳');
    };

    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
    window.addEventListener('pagehide', this.pageHideHandler);
  }

  /**
   * 移除页面可见性监听器
   */
  private removeVisibilityListeners(): void {
    if (this.visibilityChangeHandler) {
      document.removeEventListener(
        'visibilitychange',
        this.visibilityChangeHandler
      );
      this.visibilityChangeHandler = null;
    }

    if (this.pageHideHandler) {
      window.removeEventListener('pagehide', this.pageHideHandler);
      this.pageHideHandler = null;
    }
  }

  /**
   * 生成会话ID
   */
  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

export default ServiceWorkerCrashMonitor;
